import React, { useState } from "react";
import { Button, Col, Container, Row, Table } from "react-bootstrap";
import { Outlet, useLoaderData, useLocation } from "react-router";
import { Link } from "react-router-dom";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSetting } from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { getRoster, getTeam, sendWaiver } from "../../lib/api";
import { canEditTeam, defaultDateFormat } from "../../lib/utils";
import { IAPIResponse, IPlayer, ITeam } from "../../types/api";

export const loader = async ({ params }: any) => {
    const { teamId } = params;

    const [roster, { team: currentTeam }] = await Promise.all([
        getRoster(teamId),
        getTeam(teamId),
    ]);
    return {
        teamId,
        roster,
        currentTeam,
    };
};

const Roster = (): React.JSX.Element => {
    const location = useLocation();
    const isOnRosterPage = /team\/\d+\/roster\/?$/.test(location.pathname);
    const dispatch = useAppDispatch();
    const [sending, setSending] = useState<boolean>(false);

    const {
        roster: { players: loadedRoster },
        currentTeam,
    } = useLoaderData() as {
        roster: IAPIResponse & {
            players: IPlayer[];
        };
        currentTeam: ITeam;
    };
    const [roster, setRoster] = useState<IPlayer[]>(loadedRoster);

    const currentLeagueYear = useAppSelector(state =>
        selectSetting(state, "wibb_current_league_year"),
    )?.setting;

    const currentTeamsLeague = currentTeam?.leagues?.find(
        l => l.year === currentLeagueYear,
    );

    const currentTournamentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "wibb_current_league_year"),
        )?.setting,
    );

    const calculateDateAdded = (p: IPlayer) => {
        const teamYear =
            p.teams.find(t => t.year === currentTeamsLeague?.year) ||
            p.teams.find(t => t.year === currentTournamentYear);

        if (teamYear) {
            if (teamYear.primary?.id === parseInt(currentTeam.id.toString())) {
                return teamYear.primary.dateAdded
                    ? new Date(teamYear.primary.dateAdded).toLocaleDateString(
                          "en-US",
                          {
                              dateStyle: "short",
                              timeZone: "UTC",
                          },
                      )
                    : null;
            } else {
                const secondaryTeam = teamYear.secondary.find(
                    t => t.id === parseInt(currentTeam.id.toString()),
                );

                return (
                    secondaryTeam &&
                    secondaryTeam.dateAdded &&
                    new Date(secondaryTeam.dateAdded).toLocaleDateString(
                        "en-US",
                        {
                            dateStyle: "short",
                            timeZone: "UTC",
                        },
                    )
                );
            }
        }

        return null;
    };

    const handleWaiverClick = () => {
        setSending(true);
        sendWaiver(currentTeam!.id, 0)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: `Waiver sent to players`,
                        dismissible: true,
                    }),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem sending the waivers.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                setSending(false);
            });
    };

    const columns = [
        {
            label: "",
            viewable: false,
            width: ["6%", "0%"],
            render: (p: IPlayer) => (
                <Link
                    to={`/coachs-clubhouse/team/${currentTeam?.id}/roster/edit/${p.id}`}
                    className="btn btn-primary"
                >
                    Edit
                </Link>
            ),
        },
        {
            label: "Status",
            viewable: false,
            width: ["8%", "0%"],
            render: (p: IPlayer) => (p.active === 1 ? "Active" : "Inactive"),
        },
        {
            label: "Name",
            viewable: true,
            width: ["19%", "50%"],
            render: (p: IPlayer) => `${p.firstName} ${p.lastName}`,
        },
        {
            label: "#",
            viewable: true,
            width: ["5%", "16%"],
            render: (p: IPlayer) => p.uniformNumber,
        },
        {
            label: "DOB",
            viewable: false,
            width: ["9%", "0%"],
            render: (p: IPlayer) =>
                p.dob
                    ? new Date(p.dob).toLocaleDateString("en-US", {
                          dateStyle: "short",
                          timeZone: "UTC",
                      })
                    : "",
        },
        {
            label: "Email",
            width: ["18%", "0%"],
            render: (p: IPlayer) => p.email,
        },
        {
            label: "Date Added",
            viewable: false,
            width: ["9%", "0%"],
            render: (p: IPlayer) => {
                return calculateDateAdded(p);
            },
        },
        {
            label: "Waiver Date Accepted",
            viewable: false,
            width: ["12%", "0%"],
            render: (p: IPlayer) =>
                p.waiverAccepted
                    ? defaultDateFormat.format(new Date(p.waiverAccepted))
                    : "None",
        },
    ];

    return isOnRosterPage ? (
        <div data-testid="rosterContent" className="rosterContent">
            <Container fluid="xs">
                <Row>
                    <Col>
                        <h2>Current Roster</h2>
                        <Table striped className="teamRoster" responsive>
                            <thead>
                                <tr>
                                    {columns.map((column, index) => (
                                        <React.Fragment key={index}>
                                            <th>{column.label}</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {roster
                                    .sort((a, b) => {
                                        const activeComp = b.active - a.active;

                                        if (activeComp !== 0) {
                                            return activeComp;
                                        }

                                        return a.uniformNumber &&
                                            b.uniformNumber
                                            ? a.uniformNumber - b.uniformNumber
                                            : 0;
                                    })
                                    .map(p => {
                                        return (
                                            <tr key={p.id}>
                                                {columns.map(
                                                    (column, index) => (
                                                        <td
                                                            key={index}
                                                            width={
                                                                canEditTeam(
                                                                    currentTeam,
                                                                )
                                                                    ? column
                                                                          .width[0]
                                                                    : column
                                                                          .width[1]
                                                            }
                                                        >
                                                            {column.render(p)}
                                                        </td>
                                                    ),
                                                )}
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </Table>
                        {canEditTeam(currentTeam) && (
                            <>
                                <Link
                                    to={`/coachs-clubhouse/team/${currentTeam?.id}/roster/add`}
                                    className={`btn btn-primary${
                                        sending ? " disabled" : ""
                                    }`}
                                >
                                    Add Player
                                </Link>
                                <Button
                                    variant="primary"
                                    onClick={handleWaiverClick}
                                    disabled={sending}
                                >
                                    {sending
                                        ? "Sending waivers...."
                                        : "Send Waiver Request"}
                                </Button>
                            </>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    ) : (
        <Outlet context={[roster, setRoster]} />
    );
};

export default Roster;
